import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Mail, MapPin, Menu, MessageCircle, MoveRight, Phone, X } from "lucide-react";
import { Toaster, toast } from "sonner";
import "@/App.css";
import "@/ContactPage.css";

const API = process.env.REACT_APP_BACKEND_URL || "";

const offices = [
  { city: "Bengaluru", address: "12th Main, Indiranagar, Bengaluru — 560 038", phone: "+91 89258 11898" },
  { city: "Hyderabad", address: "Road No. 36, Jubilee Hills, Hyderabad — 500 033", phone: "+91 89258 11899" },
  { city: "Chennai", address: "Khader Nawaz Khan Road, Nungambakkam, Chennai — 600 006", phone: "+91 89258 11900" },
];

const reasons = [
  ["New project", "Start a full-home or modular project"],
  ["Get a quote", "Understand pricing for your brief"],
  ["Ongoing project", "Reach your project lead directly"],
  ["Partnership", "Trade, architecture, or press enquiries"],
];

export default function ContactPage() {
  const [menu, setMenu] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reason, setReason] = useState("New project");

  const whatsapp = () =>
    window.open(
      "https://wa.me/918925811898?text=Hi%20Morrow%20House%2C%20I%27d%20like%20to%20get%20in%20touch.",
      "_blank"
    );

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    const f = e.target;
    try {
      await fetch(`${API}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          source: "contact_page",
          full_name: f.name.value,
          email: f.email.value,
          phone: f.phone.value,
          city: f.city.value,
          message: `[${reason}] ${f.message.value}`,
        }),
      });
    } catch (_) { /* silent */ }
    toast.success("Message sent — we'll be in touch within one working day.");
    f.reset();
    setSubmitting(false);
  };

  return (
    <div className="site-shell">
      <Toaster position="top-center" />

      {/* ── Header ── */}
      <header className="site-header">
        <Link className="brand" to="/">
          <span className="brand-mark">M</span>
          <span>MORROW<br /><i>HOUSE</i></span>
        </Link>
        <nav className="desktop-nav">
          <Link to="/" className="nav-contact-link">Home</Link>
          <Link to="/#work" className="nav-contact-link">Our work</Link>
          <Link to="/#packages" className="nav-contact-link">Packages</Link>
          <Link to="/#process" className="nav-contact-link">Process</Link>
          <Link to="/contact" className="nav-contact-link" style={{ color: "var(--clay)" }}>Contact</Link>
        </nav>
        <Link to="/contact" className="header-cta">Get in touch <ArrowUpRight size={16} /></Link>
        <button className="menu-button" onClick={() => setMenu(!menu)} aria-label="Toggle menu">
          {menu ? <X /> : <Menu />}
        </button>
        <div className={`mobile-menu-backdrop${menu ? " is-open" : ""}`} aria-hidden="true" onClick={() => setMenu(false)} />
        <nav className={`mobile-menu${menu ? " is-open" : ""}`} aria-label="Mobile navigation">
          <div className="mobile-menu-header">
            <Link className="brand" to="/" onClick={() => setMenu(false)}>
              <span className="brand-mark">M</span>
              <span>MORROW<br /><i>HOUSE</i></span>
            </Link>
            <button className="mobile-menu-close" onClick={() => setMenu(false)} aria-label="Close menu"><X size={22} /></button>
          </div>
          <div className="mobile-menu-links">
            <Link to="/" className="mobile-menu-contact-link" onClick={() => setMenu(false)}>Home</Link>
            <Link to="/#work" className="mobile-menu-contact-link" onClick={() => setMenu(false)}>Our work</Link>
            <Link to="/#packages" className="mobile-menu-contact-link" onClick={() => setMenu(false)}>Packages</Link>
            <Link to="/#process" className="mobile-menu-contact-link" onClick={() => setMenu(false)}>Process</Link>
            <Link to="/contact" className="mobile-menu-contact-link" onClick={() => setMenu(false)} style={{ color: "var(--clay)" }}>Contact</Link>
          </div>
          <button className="mobile-menu-cta" onClick={() => { setMenu(false); whatsapp(); }}>
            WhatsApp us <ArrowUpRight size={16} />
          </button>
        </nav>
      </header>

      <main>
        {/* ── Hero banner ── */}
        <section className="contact-hero">
          <div className="contact-hero-copy">
            <p className="eyebrow">GET IN TOUCH <span>↗</span></p>
            <h1>Let's talk about<br /><em>your home.</em></h1>
            <p className="contact-hero-sub">Whether you have a clear brief or just a feeling — we'd love to hear it. A designer will respond within one working day.</p>
            <div className="contact-hero-actions">
              <a href="tel:+918925811898" className="button button-dark">
                <Phone size={16} /> Call us now
              </a>
              <button className="text-link" onClick={whatsapp}>
                <MessageCircle size={16} /> WhatsApp
              </button>
            </div>
          </div>
          <div className="contact-hero-facts">
            {[
              ["1 day", "Response time"],
              ["240+", "Homes completed"],
              ["4.9★", "Client rating"],
              ["7 yr", "Warranty included"],
            ].map(([v, l]) => (
              <div key={l} className="contact-fact">
                <strong>{v}</strong>
                <small>{l}</small>
              </div>
            ))}
          </div>
        </section>

        {/* ── Reason picker + Form ── */}
        <section className="contact-body">
          <div className="contact-reasons">
            <p className="eyebrow">WHAT BRINGS YOU HERE?</p>
            <h2>How can we<br /><em>help you?</em></h2>
            <div className="reason-list">
              {reasons.map(([label, desc]) => (
                <button
                  key={label}
                  className={`reason-item${reason === label ? " active" : ""}`}
                  onClick={() => setReason(label)}
                >
                  <span className="reason-check"><Check size={13} /></span>
                  <span>
                    <b>{label}</b>
                    <small>{desc}</small>
                  </span>
                </button>
              ))}
            </div>
            <div className="contact-direct">
              <p className="eyebrow" style={{ marginTop: 48 }}>OR REACH US DIRECTLY</p>
              <a href="tel:+918925811898" className="contact-direct-row">
                <Phone size={15} /> +91 89258 11898
              </a>
              <a href="mailto:hello@morrowhouse.in" className="contact-direct-row">
                <Mail size={15} /> hello@morrowhouse.in
              </a>
              <button className="contact-direct-row" onClick={whatsapp}>
                <MessageCircle size={15} /> WhatsApp us
              </button>
            </div>
          </div>

          <div className="contact-form-wrap">
            <p className="contact-form-label eyebrow">SEND A MESSAGE</p>
            <form className="contact-form" onSubmit={submit} noValidate>
              <label>
                Your name
                <input required name="name" placeholder="e.g. Ananya Rao" />
              </label>
              <label>
                Email address
                <input required name="email" type="email" placeholder="you@email.com" />
              </label>
              <label>
                Phone number
                <input required name="phone" type="tel" placeholder="+91 98765 43210" />
              </label>
              <label>
                City
                <select name="city" defaultValue="Bengaluru">
                  <option>Bengaluru</option>
                  <option>Hyderabad</option>
                  <option>Chennai</option>
                  <option>Other</option>
                </select>
              </label>
              <label className="wide">
                Your message
                <textarea
                  name="message"
                  required
                  placeholder={
                    reason === "New project"
                      ? "Tell us about your space, timeline and the feeling you're after…"
                      : reason === "Get a quote"
                      ? "Share your home type, size and any specific requirements…"
                      : reason === "Ongoing project"
                      ? "Your project name or address, and what you need help with…"
                      : "Tell us about the collaboration you have in mind…"
                  }
                />
              </label>
              <label className="consent wide">
                <input type="checkbox" required />
                I'm happy for Morrow House to contact me about my project.
              </label>
              <button className="button button-clay wide" type="submit" disabled={submitting}>
                {submitting ? "Sending…" : <>{reason === "Get a quote" ? "Request a quote" : "Send message"} <MoveRight size={17} /></>}
              </button>
            </form>
          </div>
        </section>

        {/* ── Office locations ── */}
        <section className="contact-offices">
          <div className="contact-offices-heading">
            <p className="eyebrow">OUR STUDIOS</p>
            <h2>Where to<br /><em>find us.</em></h2>
          </div>
          <div className="offices-grid">
            {offices.map((o) => (
              <article key={o.city} className="office-card">
                <MapPin size={18} />
                <h3>{o.city}</h3>
                <p>{o.address}</p>
                <a href={`tel:${o.phone.replace(/\s/g, "")}`}>{o.phone}</a>
              </article>
            ))}
          </div>
        </section>
      </main>

      {/* ── Footer ── */}
      <footer className="site-footer">
        <div className="footer-top">
          <div className="brand brand-light">
            <span className="brand-mark">M</span>
            <span>MORROW<br /><i>HOUSE</i></span>
          </div>
          <p>Thoughtful interiors for<br />the life you're building.</p>
          <Link to="/contact" style={{ color: "#d99b81", borderBottom: "1px solid #d99b81", paddingBottom: 6, fontSize: 13 }}>
            Get in touch <ArrowUpRight size={16} style={{ display: "inline" }} />
          </Link>
        </div>
        <div className="footer-bottom">
          <span>© 2025 Morrow House Interiors</span>
          <span>Bengaluru · Hyderabad · Chennai</span>
          <a href="mailto:hello@morrowhouse.in">hello@morrowhouse.in</a>
        </div>
      </footer>

      {/* ── Mobile sticky bar ── */}
      <div className="mobile-sticky">
        <a href="tel:+918925811898"><Phone size={16} /> Call</a>
        <button onClick={whatsapp}><MessageCircle size={16} /> WhatsApp</button>
        <button onClick={whatsapp}>Send message</button>
      </div>
    </div>
  );
}
